import React, { ReactNode, useCallback, useMemo } from "react";
import {
  Issue,
  IdType,
  SurfaceType,
  PlacementActivityChange,
} from "@adsk/acc-issues-types";
import { Sheet } from "@adsk/acc-issues-core/sheets-service/SheetsApi";
import { EntityItem } from "@adsk/docs-js-sdk";
import { SheetAssociation } from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useIssuePlacement } from "@adsk/acc-issues-core/providers/IssuePlacementContext";
import { useCurrentProductId } from "@adsk/alloy-provider";
import { getPlacementOriginContext } from "@adsk/acc-issues-core/utils/data/placement";
import {
  DOCUMENT_SURFACE_TYPE,
  SHEET_SURFACE_TYPE,
  getHasSheetAssociationFetchErroredByIssueId,
  getIsFetchedSheetAssociationByIssueId,
  getSheetAssociationByIssueId,
  getSheetAssociationFetchStatusByIssueId,
} from "@adsk/acc-issues-core/state/redux/ducks/sheets";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { CouldNotDisplayPlacement } from "@adsk/acc-issues-common/components/display/CouldNotDisplayPlacement";
import {
  getHasSheetFetchErroredBySheetId,
  getSheetFetchStatusBySheetId,
  getSheetBySheetId,
} from "@adsk/acc-issues-core/state/redux/ducks/sheets";
import {
  getDocumentByDocumentVersionId,
  getDocumentFetchStatusByUrn,
  getDocumentFetchStatusByVersionId,
  getDocumentsByUrn,
} from "@adsk/acc-issues-core/state/redux/ducks/documents";
import { FetchStatus } from "@adsk/acc-issues-core/state/redux/fetchHelpers";
import { getDocumentUrnOfIssue } from "@adsk/acc-issues-common/hooks/data/documentHooks";

export interface PlacementData {
  placementText?: string | ReactNode;
  placementLink: string;
  sheet?: Sheet;
  document?: EntityItem;
  sheetAssociation?: SheetAssociation;
  isSheetAssociationFetched: boolean;
  isPlacementLoading: boolean;
  hasPlacementError: boolean;
  isPlacementDeleted: boolean;
}

export type PlacementDataIssue = Pick<
  Issue,
  "id" | "projectId" | "placements" | "linkedDocuments"
>;

type PlacementDataGetter = (issue?: PlacementDataIssue) => PlacementData;

const usePlacementData = (): PlacementDataGetter => {
  const { T } = useTranslations();
  const productId = useCurrentProductId();
  const getPlacementLink = useIssuePlacement();

  const selectIsFetchedSheetAssociationByIssueId = useSelector(
    getIsFetchedSheetAssociationByIssueId
  );
  const selectHasSheetAssociationFetchErroredByIssueId = useSelector(
    getHasSheetAssociationFetchErroredByIssueId
  );
  const selectSheetAssociationFetchStatusByIssueId = useSelector(
    getSheetAssociationFetchStatusByIssueId
  );
  const selectSheetAssociationByIssueId = useSelector(
    getSheetAssociationByIssueId
  );

  const selectSheet = useSelector(getSheetBySheetId);
  const selectSheetFetchStatusBySheetId = useSelector(
    getSheetFetchStatusBySheetId
  );

  const selectDocumentByVersionId = useSelector(getDocumentByDocumentVersionId);
  const selectDocumentFetchStatusByUrn = useSelector(
    getDocumentFetchStatusByUrn
  );
  const selectDocumentFetchStatusByVersionId = useSelector(
    getDocumentFetchStatusByVersionId
  );
  const documentsByUrn = useSelector(getDocumentsByUrn);

  return useCallback(
    (issue?: PlacementDataIssue): PlacementData => {
      if (!issue) {
        return {
          placementText: undefined,
          placementLink: "",
          sheet: undefined,
          document: undefined,
          sheetAssociation: undefined,
          isSheetAssociationFetched: false,
          isPlacementLoading: false,
          hasPlacementError: false,
          isPlacementDeleted: false,
        };
      }

      const documentUrn = getDocumentUrnOfIssue(issue);
      const documentRetrievedByUrn =
        typeof documentUrn === "string"
          ? documentsByUrn[documentUrn]
          : undefined;

      const isSheetAssociationFetched =
        selectIsFetchedSheetAssociationByIssueId(issue.id) ?? false;
      const hasSheetAssociationFetchErrored =
        selectHasSheetAssociationFetchErroredByIssueId(issue.id) ?? false;
      const sheetAssociationFetchStatus =
        selectSheetAssociationFetchStatusByIssueId(issue.id) ??
        FetchStatus.Initial;

      const sheetAssociation =
        selectSheetAssociationByIssueId(issue.id) ?? undefined;

      const sheetId =
        sheetAssociation?.surfaceType === SHEET_SURFACE_TYPE
          ? sheetAssociation.sheetId
          : undefined;
      const sheetFetchStatus =
        selectSheetFetchStatusBySheetId(sheetId) ?? FetchStatus.Initial;
      const sheet = selectSheet(sheetId);

      const documentVersionId =
        sheetAssociation?.surfaceType === DOCUMENT_SURFACE_TYPE
          ? sheetAssociation.documentVersionId
          : undefined;
      const documentRetrievedByVersionId =
        selectDocumentByVersionId(documentVersionId);

      const documentFetchStatusByUrn =
        selectDocumentFetchStatusByUrn(documentUrn) ?? FetchStatus.Initial;
      const documentFetchStatusByVersionId =
        selectDocumentFetchStatusByVersionId(documentVersionId) ??
        FetchStatus.Initial;

      const hasDocumentVersionError =
        !!documentVersionId &&
        documentFetchStatusByVersionId === FetchStatus.Error;

      const document = documentRetrievedByUrn || documentRetrievedByVersionId;

      const isEntityLoading = (status: FetchStatus, entity: unknown) =>
        status === FetchStatus.Loading ||
        (status === FetchStatus.Initial && entity === undefined);

      const isEntityDeleted = (
        status: FetchStatus,
        entity: unknown,
        hasError: boolean
      ) => status === FetchStatus.Success && entity === undefined && !hasError;

      const isSheetPlacementLoading =
        !!sheetId && isEntityLoading(sheetFetchStatus, sheet);
      const isSheetPlacementErrored =
        !!sheetId && sheetFetchStatus === FetchStatus.Error;

      const isDocumentByUrnLoading =
        !!documentUrn &&
        isEntityLoading(documentFetchStatusByUrn, documentRetrievedByUrn);
      const isDocumentByUrnErrored =
        !!documentUrn && documentFetchStatusByUrn === FetchStatus.Error;
      const isDocumentByUrnDeleted =
        !!documentUrn &&
        isEntityDeleted(
          documentFetchStatusByUrn,
          documentRetrievedByUrn,
          false
        );

      const isDocumentByVersionIdLoading =
        !!documentVersionId &&
        isEntityLoading(
          documentFetchStatusByVersionId,
          documentRetrievedByVersionId
        );
      const isDocumentByVersionIdErrored =
        !!documentVersionId &&
        documentFetchStatusByVersionId === FetchStatus.Error;
      const isDocumentByVersionIdDeleted =
        !!documentVersionId &&
        isEntityDeleted(
          documentFetchStatusByVersionId,
          documentRetrievedByVersionId,
          hasDocumentVersionError
        );

      const isAssociationFetchErroredAndBlocksPlacement =
        hasSheetAssociationFetchErrored && !documentUrn;

      const isPlacementLoading =
        sheetAssociationFetchStatus === FetchStatus.Loading ||
        isSheetPlacementLoading ||
        isDocumentByUrnLoading ||
        isDocumentByVersionIdLoading;

      const hasPlacementError =
        isAssociationFetchErroredAndBlocksPlacement ||
        isSheetPlacementErrored ||
        isDocumentByUrnErrored ||
        isDocumentByVersionIdErrored;

      // Only surface "deleted" for document placements (not sheets) for now.
      const isPlacementDeleted =
        isDocumentByUrnDeleted || isDocumentByVersionIdDeleted;

      const placementLink = getPlacementLink({
        projectId: issue.projectId,
        document,
        sheet,
        issueId: issue.id,
        productId,
        originContext: getPlacementOriginContext(issue),
      });

      const documentName =
        document?.name ||
        (T.translate(
          "issueEditForm.placement.missingDocumentNamePrompt"
        ) as string);

      const sheetName = sheet
        ? sheet.name + (sheet.title ? ` (${sheet.title})` : "")
        : undefined;

      const hasSheetPlacementAssociation =
        sheetAssociation?.surfaceType === SHEET_SURFACE_TYPE;

      let placementText: string | ReactNode | undefined;
      if (sheet) {
        placementText = sheetName;
      } else if (document) {
        placementText = documentName;
      } else if (hasSheetPlacementAssociation && placementLink) {
        placementText = T.translate(
          "issueEditForm.placement.missingSheetNamePrompt"
        ) as string;
      } else {
        placementText = undefined;
      }

      return {
        placementText,
        placementLink,
        sheet: sheet ?? undefined,
        document,
        sheetAssociation,
        isSheetAssociationFetched,
        isPlacementLoading,
        hasPlacementError,
        isPlacementDeleted,
      };
    },
    [
      T,
      documentsByUrn,
      getPlacementLink,
      productId,
      selectDocumentByVersionId,
      selectDocumentFetchStatusByUrn,
      selectDocumentFetchStatusByVersionId,
      selectHasSheetAssociationFetchErroredByIssueId,
      selectIsFetchedSheetAssociationByIssueId,
      selectSheet,
      selectSheetAssociationByIssueId,
      selectSheetAssociationFetchStatusByIssueId,
      selectSheetFetchStatusBySheetId,
    ]
  );
};

export default usePlacementData;

export const usePlacementDataForActivityPlacement = ({
  placement,
}: {
  placement: PlacementActivityChange;
}): {
  hasPlacementError: boolean;
  isPlacementLoading: boolean;
  isPlacementDeleted: boolean;
  sheet?: Sheet;
  document?: EntityItem;
  placementText: string | ReactNode;
  idType: IdType;
  isSheet: boolean;
} => {
  const { T } = useTranslations();
  const { id, surfaceType, idType: idTypeProp } = placement;
  const isSheet = surfaceType === SurfaceType.Sheet;
  const idType = idTypeProp ?? (isSheet ? IdType.SheetId : IdType.DocumentId);

  const selectSheetById = useSelector(getSheetBySheetId);
  const selectHasSheetFetchErroredBySheetId = useSelector(
    getHasSheetFetchErroredBySheetId
  );
  const selectSheetFetchStatusBySheetId = useSelector(
    getSheetFetchStatusBySheetId
  );
  const selectDocumentById = useSelector(getDocumentByDocumentVersionId);
  const selectDocumentFetchStatusByVersionId = useSelector(
    getDocumentFetchStatusByVersionId
  );
  const selectDocumentFetchStatusByUrn = useSelector(
    getDocumentFetchStatusByUrn
  );
  const documentsByUrn = useSelector(getDocumentsByUrn);

  const isSheetPlacement = idType === IdType.SheetId;
  const isDocumentByVersionIdPlacement = idType === IdType.DocumentId;
  const isDocumentByUrnPlacement = idType === IdType.LineageUrn;

  const sheetFetchStatus = isSheetPlacement
    ? selectSheetFetchStatusBySheetId(id) ?? FetchStatus.Initial
    : FetchStatus.Initial;
  const hasSheetFetchErrored = isSheetPlacement
    ? selectHasSheetFetchErroredBySheetId(id) ?? false
    : false;

  const documentFetchStatus = isDocumentByVersionIdPlacement
    ? selectDocumentFetchStatusByVersionId(id) ?? FetchStatus.Initial
    : isDocumentByUrnPlacement
    ? selectDocumentFetchStatusByUrn(id) ?? FetchStatus.Initial
    : FetchStatus.Initial;

  // Any per-item document error should surface as "Couldn't display".
  // "Deleted" is only when the request succeeded, the entity is missing, and there is no error.
  const hasDocumentFetchErrored =
    (isDocumentByVersionIdPlacement || isDocumentByUrnPlacement) &&
    documentFetchStatus === FetchStatus.Error;

  let sheet: Sheet | undefined;
  let document: EntityItem | undefined;
  switch (idType) {
    case IdType.SheetId:
      sheet = selectSheetById(id) ?? undefined;
      break;
    case IdType.DocumentId:
      document = selectDocumentById(id) ?? undefined;
      break;
    case IdType.LineageUrn:
      document = documentsByUrn[id];
      break;
  }

  const isSheetPlacementLoading =
    isSheetPlacement && sheetFetchStatus === FetchStatus.Loading;
  const isDocumentPlacementLoading =
    (isDocumentByVersionIdPlacement || isDocumentByUrnPlacement) &&
    documentFetchStatus === FetchStatus.Loading;

  const isSheetPlacementDeleted =
    isSheetPlacement &&
    sheetFetchStatus === FetchStatus.Success &&
    sheet === undefined;
  const isDocumentPlacementDeleted =
    (isDocumentByVersionIdPlacement || isDocumentByUrnPlacement) &&
    documentFetchStatus === FetchStatus.Success &&
    document === undefined;

  const placementText = useMemo(() => {
    if (hasSheetFetchErrored || hasDocumentFetchErrored) {
      return <CouldNotDisplayPlacement />;
    }

    if (isSheetPlacement) {
      if (sheet?.name) {
        return `${sheet.name}${sheet.title ? ` (${sheet.title})` : ""}`;
      }
      if (isSheetPlacementDeleted) {
        return T.translate("activityLog.sheetWasDeleted") as string;
      }
      if (isSheetPlacementLoading) {
        return "";
      }
      return "";
    }

    if (document?.name) {
      return document.name;
    }
    if (isDocumentPlacementDeleted) {
      return T.translate("activityLog.fileWasDeleted") as string;
    }
    if (isDocumentPlacementLoading) {
      return "";
    }
    return "";
  }, [
    T,
    document?.name,
    hasDocumentFetchErrored,
    hasSheetFetchErrored,
    isDocumentPlacementDeleted,
    isDocumentPlacementLoading,
    isSheetPlacement,
    isSheetPlacementDeleted,
    isSheetPlacementLoading,
    sheet?.name,
    sheet?.title,
  ]);

  return {
    hasPlacementError: hasSheetFetchErrored || hasDocumentFetchErrored,
    isPlacementLoading: isSheetPlacementLoading || isDocumentPlacementLoading,
    isPlacementDeleted: isSheetPlacementDeleted || isDocumentPlacementDeleted,
    sheet,
    document,
    placementText,
    idType,
    isSheet,
  };
};
